import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useUIStore } from './useUIStore'; // 🟢 Import useUIStore instead of modalStore
import apiClient from '../api/api.js'; // 🟢 CORRECCIÓN: La ruta correcta es apiClient.js
import { useAuthStore } from './useAuthStore.js';

export const usePodcastStore = create(
  persist(
    (set, get) => ({
      // Estado Inicial
      currentPodcast: null, // Objeto del podcast actual { _id, title, audioUrl, coverImage, isPremium, authorDetails }
      isPlaying: false,
      isLocked: false, // Estado del Paywall
      queue: [], // Cola de reproducción
      progress: {}, // Persistencia: { podcastId: currentTime }
      volume: 1.0,
      duration: 0,
      currentTime: 0,
      streamToken: null, // 🟢 Estado para el token de acceso
      reactions: [], // 🟢 Estado para las reacciones flotantes
      topDonors: [], // 🟢 Estado para el Muro de la Fama
      podcastGiftAnimation: null, // 🟢 Estado para la animación de regalos
      giftCatalog: [],
      giftCatalogLoading: false,
      giftCatalogError: null,

      // Acciones de Reproducción
      setVolume: (volume) => set({ volume }),
      
      setDuration: (duration) => set({ duration }),
      
      setCurrentTime: (time) => set({ currentTime: time }),

      // Lógica de Paywall y Tokens
      playPodcast: async (podcast, user) => {
        const { progress, addToQueue } = get();

        // Si el podcast no es premium, reproducir directamente
        if (!podcast.isPremium) {
          set({ 
            currentPodcast: podcast, 
            isLocked: false, 
            isPlaying: true,
            streamToken: null,
            currentTime: progress[podcast._id] || 0 
          });
          addToQueue(podcast);
          return;
        }

        // Si es premium, solicitar token de acceso
        try {
          // Asumimos un endpoint que devuelve un token temporal
          const response = await apiClient.post(`/podcasts/${podcast._id}/stream-token`);
          const { token } = response.data;

          if (token) {
            // ✅ ACCESO CONCEDIDO
            set({ 
              currentPodcast: podcast, 
              isLocked: false, 
              isPlaying: true,
              streamToken: token, // Guardamos el token
              currentTime: progress[podcast._id] || 0 
            });
            addToQueue(podcast);
          }
        } catch (error) {
          // 🛑 BLOQUEO DE PAYWALL
          console.error("Paywall: Access denied.", error);
          set({ 
            currentPodcast: podcast, 
            isLocked: true, 
            isPlaying: false,
            streamToken: null
          });
          // Disparamos el modal de suscripción
          useUIStore.getState().openModal('subscriptionModal', {
            contentId: podcast._id,
            contentType: 'podcast'
          });
        }
      },

      // 🟢 Lógica de Reacciones (Social Flow)
      sendReaction: (emoji) => {
        const { reactions } = get();
        const id = Date.now() + Math.random();
        const x = Math.random() * 60 + 20; // Posición horizontal aleatoria (20% - 80%)
        
        const newReaction = { id, emoji, x, y: 0 };
        
        // 🟢 OPTIMIZACIÓN: Limitamos a las últimas 50 reacciones en pantalla para evitar lag
        const updatedReactions = [...reactions, newReaction];
        if (updatedReactions.length > 50) {
            updatedReactions.shift();
        }
        set({ reactions: updatedReactions });

        // Limpieza automática después de la animación (2s)
        setTimeout(() => {
          set((state) => ({
            reactions: state.reactions.filter((r) => r.id !== id),
          }));
        }, 2000);
      },

      fetchGiftCatalog: async () => {
        const { giftCatalogLoading, giftCatalog } = get();
        if (giftCatalogLoading || giftCatalog.length > 0) return giftCatalog;

        set({ giftCatalogLoading: true, giftCatalogError: null });
        try {
          const response = await apiClient.get('/finance/gifts');
          const catalog = Array.isArray(response.data?.gifts) ? response.data.gifts : [];
          set({ giftCatalog: catalog, giftCatalogLoading: false });
          return catalog;
        } catch (error) {
          set({
            giftCatalogLoading: false,
            giftCatalogError: error.response?.data?.message || 'No se pudo cargar el catalogo de regalos',
          });
          return [];
        }
      },

      // 🟢 Lógica de Regalos Premium
      sendPodcastGift: async (gift) => {
        const { currentPodcast, currentTime } = get();
        const { currentUser, setCurrentUser } = useAuthStore.getState();

        if (!currentPodcast) return toast.error("No hay un podcast activo.");
        // 🟢 UNIFICADO: Usar walletBalance en lugar de coins
        const currentBalanceMinor = Number.isInteger(currentUser.walletBalanceMinor)
          ? currentUser.walletBalanceMinor
          : Math.round((Number(currentUser.walletBalance || 0) + Number.EPSILON) * 100);
        if (currentBalanceMinor < gift.amountMinor) {
          toast.error("Saldo insuficiente. ¡Recarga para apoyar al creador!");
          useUIStore.getState().openModal('rechargeModal');
          return;
        }

        try {
          // Notificación al backend
          const response = await apiClient.post(`/podcasts/${currentPodcast._id}/gift`, {
            giftId: gift.id,
            timestamp: currentTime,
          });

          const nextBalanceMinor = response.data?.walletBalanceMinor;
          const nextBalanceMajor = response.data?.walletBalance;
          if (Number.isInteger(nextBalanceMinor)) {
            setCurrentUser({
              ...currentUser,
              walletBalanceMinor: nextBalanceMinor,
              walletBalance: Number.isFinite(nextBalanceMajor)
                ? nextBalanceMajor
                : Math.round(((nextBalanceMinor / 100) + Number.EPSILON) * 100) / 100,
            });
          }

          // Disparamos la animación y actualizamos el muro de la fama
          set((state) => ({
            podcastGiftAnimation: { gift, sender: currentUser },
            topDonors: [currentUser, ...state.topDonors.filter(u => u._id !== currentUser._id)].slice(0, 5) // Añade al inicio y mantiene 5
          }));
          setTimeout(() => set({ podcastGiftAnimation: null }), 5000);

        } catch (error) {
          toast.error(error.response?.data?.message || "No se pudo enviar el regalo.");
        }
      },

      togglePlay: () => set((state) => ({ isPlaying: !state.isPlaying })),

      // Gestión de Cola
      addToQueue: (podcast) => {
        const { queue } = get();
        if (!queue.find(p => p._id === podcast._id)) {
          set({ queue: [...queue, podcast] });
        }
      },

      playNext: () => {
        const { queue, currentPodcast } = get();
        const currentIndex = queue.findIndex(p => p._id === currentPodcast?._id);
        if (currentIndex !== -1 && currentIndex < queue.length - 1) {
          // Al cambiar internamente, asumimos que el usuario ya validó acceso al entrar a la cola
          set({ currentPodcast: queue[currentIndex + 1], isPlaying: true });
        }
      },

      // Persistencia de Progreso
      saveProgress: (podcastId, time) => set((state) => ({
        progress: { ...state.progress, [podcastId]: time },
        currentTime: time
      })),
    }),
    {
      name: 'podcast-storage', // Nombre en localStorage
      partialize: (state) => ({ progress: state.progress, volume: state.volume, queue: state.queue }), // Solo persistimos lo necesario
    }
  )
);